import axios from "axios";
import Keycloak from 'keycloak-js';
import { Cookies } from "react-cookie";
import Dispatcher from "../../../api/Dispatcher";


const rootURL = (() => {
  if (process.env.NODE_ENV === "production") {
    if (window.config.vendor.ignorePreForKeycloakURL) {
      return `https://${window.config.vendor.keycloak}`;
    } else {
      return `https://${window.config.host.pre}.${window.config.vendor.keycloak}`;
    }
  } else {
    return `https://${window.config.vendor.keycloak}`;
  }
})();

const kc = new Keycloak({
        url: rootURL,
        realm: window.config.host.KeycloakRealm,
        clientId: window.config.host.clientId,
    });

const cookies = new Cookies();

// URL prefixes that point at our own backend and should carry the Keycloak
// access token. Requests to any other host (CDN, third parties) are left alone.
const ownApiBases = () =>
    [
        window.config?.vendor?.api,
        window.config?.host?.api,
        window.config?.host?.uploadurl,
    ].filter(Boolean);

const isOwnApiRequest = (url) => {
    if (!url) return false;
    return ownApiBases().some((base) => url.startsWith(base));
};

// Loading overlay: count in-flight requests to our backend and broadcast
// whether any are pending. Background/auto calls opt out via `skipLoading`.
let pendingRequests = 0;

const emitLoading = () => {
    Dispatcher.dispatch("loading:change", pendingRequests > 0);
};

let axiosInterceptorRegistered = false;

const registerAxiosInterceptors = () => {
    if (axiosInterceptorRegistered) return;
    axiosInterceptorRegistered = true;

    axios.interceptors.request.use(async (config) => {
        // Only our backend gets the token, and only while authenticated
        // (public flows like password/TOTP reset run while logged out).
        if (kc.authenticated && isOwnApiRequest(config.url)) {
            try {
                // Refresh if the token has < 30s of life left, then attach it.
                await kc.updateToken(30);
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${kc.token}`;
            } catch (err) {
                // Refresh failed → the session is gone; drop the user out.
                SessionData.logout();
                return Promise.reject(err);
            }
        }

        // Show the overlay for user-instigated backend calls only. Pollers and
        // other background calls pass `skipLoading: true` in their axios config.
        if (!config.skipLoading && isOwnApiRequest(config.url)) {
            config._countedLoading = true;
            pendingRequests += 1;
            emitLoading();
        }
        return config;
    });

    const settleLoading = (config) => {
        if (config && config._countedLoading) {
            pendingRequests = Math.max(0, pendingRequests - 1);
            emitLoading();
        }
    };

    axios.interceptors.response.use(
        (response) => {
            settleLoading(response.config);
            return response;
        },
        (error) => {
            settleLoading(error.config);
            return Promise.reject(error);
        }
    );
};

const SessionData = {
    clientInstance: null,
    currentUser: null,
    currentUserInfo: null,
    realmHash: window.config.host.KeycloakRealm,
    initialized: false,

    isLoggedIn: () => {
        return SessionData.clientInstance != null
    },
    isLoggedOut: () => {
        return SessionData.clientInstance != null && SessionData.clientInstance.state.info == null
    },

  logout: () => {
    return new Promise((resolve, reject) => {
        if (SessionData.isLoggedIn()) {
            if (SessionData.clientInstance) {
                cookies.remove("kc_token", { path: "/" });
                cookies.remove("kc_refreshToken", { path: "/" });
        
                SessionData.currentUser = null;
                SessionData.initialized = false;
                try
                {
                    SessionData.clientInstance.logout({ redirectUri: window.location });
                }
                catch(err)
                {

                }
                finally
                {
                    SessionData.clientInstance = null;
                }
            resolve()
                }
            } else {
                resolve()
            }
        })
    },

    getUsername: () => {
        if (SessionData.isLoggedIn()) {
            return SessionData.clientInstance.state.info.preferred_username;
        }
        return null;
    },

    getUserID: () => {
        if (SessionData.isLoggedIn()) {
            return SessionData.currentUser.sub;
        }
        return "";
    },

    isKeycloakRedirect() {
        const params = new URLSearchParams(window.location.search);
        return params.has("state") && params.has("session_state") && params.has("code");
    },

    init: async () => {

        if (SessionData.initialized) return;
        SessionData.initialized = true;
        registerAxiosInterceptors();
        const savedToken = cookies.get("kc_token");
        const savedRefreshToken = cookies.get("kc_refreshToken");

        const saveTokens = () => {
            cookies.set("kc_token", kc.token, { path: "/", secure: true, sameSite: "Lax" });
            cookies.set("kc_refreshToken", kc.refreshToken, { path: "/", secure: true, sameSite: "Lax" });
        };

        const scheduleRefresh = () => {
          setInterval(() => {
            kc.updateToken(70)
            .then((refreshed) => {
              if (refreshed) {
                saveTokens();
              }
            })
            .catch((err) =>
            {
                SessionData.logout();
            }
            );
          }, 30000);
        };

        if (savedToken && savedRefreshToken && !SessionData.isKeycloakRedirect()) {
          // Initialize using existing cookies
          await kc.init({
            onLoad: "check-sso",
            checkLoginIframe: false,
            token: savedToken,
            refreshToken: savedRefreshToken,
            silentCheckSsoRedirectUri: null,
          });

          if (!kc.authenticated) {
            kc.login();
            return;
          }

          saveTokens();
          scheduleRefresh();
        }
        else {
          // No cookies → force redirect login
          await kc.init({
            onLoad: "login-required",
            checkLoginIframe: false,
          });

          saveTokens();
          scheduleRefresh();
        }

        if (SessionData.isKeycloakRedirect()) {
            const url = new URL(window.location.href);
            url.search = "";
            window.history.replaceState({}, document.title, url.toString());
        }        
        SessionData.clientInstance = kc;
        SessionData.currentUser = kc.tokenParsed; // contains sub, preferred_username, etc.        
  },
}

const AdminData = {
    getCurrentUserInfo: (overwrite) => {
        /*
        if (SessionData.currentUserInfo != null && !overwrite) {
            return new Promise((resolve, reject) => { resolve(SessionData.currentUserInfo) })
        }
        */
        return new Promise((resolve, reject) => {
            axios.get(window.config.vendor.api + "/keycloak/getVithroughUserInfo?UserID=" + SessionData.currentUser.sub +"&Realm=" + window.config.host.KeycloakRealm, { skipLoading: true })
            .then(function (response) {
                const data = response.data;
                if(data.Success){
                    var newuser = JSON.parse(data.userinfo);                    
                    SessionData.currentUserInfo = newuser;
                if (SessionData.currentUserInfo.attributes == null){
                    SessionData.currentUserInfo.attributes = {}
                }
                if (SessionData.currentUserInfo.groups == null){
                    SessionData.currentUserInfo.groups = []
                }
                resolve(newuser);
                }else{
                    reject(data);
                }
            })
            .catch((err) => {
                console.error(err);
                reject(err);
            })
            
                
        })
    },
}

// Create the config object to export
const UserService = {
  SessionData,
  AdminData
};

export default UserService